// src/pages/PrivacyPolicy.js — Chrome Web Store compliant
import React from 'react';
import '../styles/pages/Page.css';
import '../styles/pages/LegalPage.css';

const PERMISSIONS = [
  { name: 'activeTab', reason: "To read the current page's DOM during recording and playback." },
  { name: 'tabs', reason: "To identify the active tab's URL for context-aware guide behaviour." },
  { name: 'storage / unlimitedStorage', reason: 'To save session state, auth token, and recording state locally in your browser.' },
  { name: 'scripting', reason: 'To inject the recording and playback interface into web pages.' },
  { name: 'host_permissions', reason: 'To operate the extension on the pages where you use it.' },
];

const PrivacyPolicy = () => {
  return (
    <div className="page-container">
      <div className="container legal-container">

        <div className="legal-header">
          <p className="m-eyebrow">Legal</p>
          <h1 className="page-title">Privacy Policy</h1>
          <p className="legal-last-updated">Last updated: March 8, 2026</p>
        </div>

        <div className="legal-body">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to michii ("we," "our," or "the Extension"). We are committed to
              protecting your privacy. This Privacy Policy explains how michii collects,
              uses, and handles information when you use our Chrome Extension to record,
              play back, and share step-by-step web guides.
            </p>
            <p>
              The use of information received from browser APIs will adhere to the{' '}
              <a
                href="https://developer.chrome.com/docs/webstore/program-policies/user-data-faq/"
                target="_blank"
                rel="noopener noreferrer"
                className="legal-inline-link"
              >
                Chrome Web Store User Data Policy
              </a>
              , including the Limited Use requirements.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>
            <p>michii only collects the minimum data necessary to provide its features.</p>

            <h3 className="legal-subheading">a) Website Content &amp; UI Data</h3>
            <p>
              When you actively start a recording, the extension reads the DOM (Document
              Object Model) of your active tab to identify interactive elements (buttons,
              inputs, links). This data is used only to construct the guide step and is
              not stored beyond the active recording session unless you explicitly save the guide.
            </p>

            <h3 className="legal-subheading">b) Screenshots</h3>
            <p>
              During guide recording, the extension captures screenshots of your active tab
              to attach visual context to each recorded step. Screenshots are stored as part
              of the guide data on our servers only when you explicitly save a guide. You can
              delete guides and their associated screenshots at any time.
            </p>

            <h3 className="legal-subheading">c) Active Tab Metadata</h3>
            <p>
              We use the <code className="legal-code">tabs</code> and{' '}
              <code className="legal-code">activeTab</code> permissions to read the URL and
              title of the page you are currently viewing, to provide context-aware guide
              playback and recording.
            </p>

            <h3 className="legal-subheading">d) User Account Data</h3>
            <p>
              When you register, we collect your email address and a securely hashed password.
            </p>

            <h3 className="legal-subheading">e) User Inputs</h3>
            <p>
              We collect the text you type into the michii chat interface and the step
              descriptions you provide when recording a guide.
            </p>

            <h3 className="legal-subheading">f) Local Storage &amp; Session State</h3>
            <p>
              We use the <code className="legal-code">storage</code> and{' '}
              <code className="legal-code">unlimitedStorage</code> permissions to save your
              authentication session, local preferences, and temporary recording state within
              your own browser. This data never leaves your device unless you explicitly save
              a guide.
            </p>

            <h3 className="legal-subheading">g) Technical Log Data</h3>
            <p>
              We may collect basic technical information (browser version, extension version)
              to troubleshoot bugs and improve performance. This data is not linked to your
              identity.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>Your data is used solely for the following purposes:</p>
            <ul>
              <li><strong>Core functionality:</strong> To record, store, and play back the guides you create.</li>
              <li><strong>Sharing:</strong> To enable guide sharing with other users you explicitly invite, or to make guides publicly visible in the Explore section when you choose to do so.</li>
              <li><strong>Processing:</strong> User inputs and relevant page data are sent to our secure backend hosted on Railway to be processed and stored.</li>
              <li><strong>Improvement:</strong> To resolve technical issues and improve the reliability of the extension.</li>
            </ul>
            <p>
              We do <strong>not</strong> use your data for advertising, profiling, or any
              purpose unrelated to providing michii's core functionality.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Data Storage and Security</h2>
            <ul>
              <li><strong>Transmission:</strong> All data sent between the extension and our backend is encrypted using industry-standard TLS/HTTPS. No unencrypted transmission of personal data occurs.</li>
              <li><strong>Storage at rest:</strong> User data stored on our servers is protected using industry-standard security measures.</li>
              <li><strong>Browsing history:</strong> We do not store your browsing history. Page content read during recording is processed in real-time and is not retained unless you explicitly save a guide.</li>
              <li><strong>Processing time:</strong> Page content and screenshots captured during a session are held in temporary browser memory only. Upon saving, they are persisted to your guide. Upon discarding, they are deleted immediately.</li>
              <li><strong>Local data:</strong> Authentication tokens and recording state are stored in your browser's local storage and the extension's local storage (<code className="legal-code">chrome.storage.local</code>). This data remains on your device.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Data Sharing &amp; Third Parties</h2>
            <ul>
              <li><strong>No sale of data:</strong> We never sell, rent, or trade your personal data or browsing history to third parties.</li>
              <li><strong>Backend:</strong> All guide data is processed and stored on our own backend infrastructure hosted on Railway. No guide content is shared with third-party analytics or advertising platforms.</li>
              <li><strong>AI processing:</strong> If AI-powered screen analysis features are used, relevant page snapshots may be sent to our backend for processing. These are not retained beyond the immediate request.</li>
              <li><strong>Legal requirements:</strong> We may disclose data if required by law or to protect the rights and safety of our users.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Permissions We Request and Why</h2>
            <p>
              michii only activates on pages where you explicitly open or use the extension.
              We do not run background processes that monitor your browsing.
            </p>
            <div className="legal-table-wrapper">
              <table className="legal-table">
                <thead>
                  <tr>
                    <th>Permission</th>
                    <th>Why it is needed</th>
                  </tr>
                </thead>
                <tbody>
                  {PERMISSIONS.map((p) => (
                    <tr key={p.name}>
                      <td><code className="legal-code">{p.name}</code></td>
                      <td>{p.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="legal-section">
            <h2>7. Your Controls and Rights</h2>
            <ul>
              <li><strong>On-demand access:</strong> michii only reads page content when you actively start a recording or trigger a playback. It does not monitor your browsing passively.</li>
              <li><strong>Delete your data:</strong> You can delete any guide and its associated screenshots from the My Guides page at any time.</li>
              <li><strong>Delete your account:</strong> You can request full account deletion, which permanently removes all your guides and personal data, by contacting us at the address below.</li>
              <li><strong>Revoke extension access:</strong> You can disable or remove the extension at any time via Chrome's extension settings at <code className="legal-code">chrome://extensions</code>.</li>
              <li><strong>Data access:</strong> You may request a copy of the data we hold about you by contacting us.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Children's Privacy</h2>
            <p>
              michii is not directed at children under the age of 13. We do not knowingly
              collect personal information from children. If you believe a child has provided
              us with personal data, please contact us and we will delete it promptly.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy to reflect changes in our practices or legal
              requirements. We will notify users of material changes by updating the
              "Last Updated" date at the top of this document. Continued use of the extension
              after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices,
              please contact us:
            </p>
            <ul>
              <li>
                <strong>Email:</strong>{' '}
                <a href="mailto:michii.support@gmail.com" className="legal-inline-link">
                  michii.support@gmail.com
                </a>
              </li>
              <li>
                <strong>Website:</strong>{' '}
                <a href="/contact" className="legal-inline-link">
                  michii.app/contact
                </a>
              </li>
            </ul>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
